import { gql } from '@apollo/client'
import Walkthrough from '../../models/Walkthrough'

export const UPLOAD_AUDIO = gql`
  mutation uploadAudio($file: Upload!, $walkthroughId: ID!) {
    uploadAudio(file: $file, walkthroughId: $walkthroughId) {
      id
      uuid
      audioUrl
    }
  }
`

export const CREATED_WALKTHROUGHS_QUERY = gql`
  query getCreatedWalkthroughs(
    $page: Int
    $perPage: Int
    $sortBy: String
    $sortDirection: String
    $contentType: ContentType
  ) {
    items: myLibrary(
      page: $page
      perPage: $perPage
      sortBy: $sortBy
      sortDirection: $sortDirection
      contentType: $contentType
    ) {
      ... on Walkthrough {
        id
        uuid
        name
        description
        steps
        meta
        createdBy {
          firstName
          lastName
          id
        }
        audioEnabled
        hasAudio
        canEdit
        canMove
        isOwner
        isInMyLibrary
        isInMyOrganizationLibrary
        canEditSharing
        canDelete
        sharedAccess
        completed
        createdAt
        canEditSharing
        viewCount
        interactionCount
        skillSet {
          id
          name
          uuid
          sharedAccess
          __typename
        }
        __typename
      }
      ... on SkillSet {
        id
        name
        uuid
        completed
        canEdit
        canMove
        isInMyLibrary
        isInMyOrganizationLibrary
        sharedAccess
        isAssigned
        isInMyLibrary
        isInMyOrganizationLibrary
        canEditSharing
        walkthroughs {
          id
          name
          uuid
          steps
          completed
          meta
        }
        __typename
      }
    }
    total: myLibraryMeta(contentType: $contentType) {
      count
    }
  }
`

export interface CreatedWalkthroughsVars {
  page?: number
  perPage?: number
  sortBy?: string
  sortDirection?: string
  contentType?: string
}

export interface WalkthroughsData {
  items: Walkthrough[]
  total: {
    count: number
  }
}

export const GET_WALKTHROUGH_QUERY = gql`
  query walkthroughByUuid($uuid: ID!) {
    walkthrough: walkthroughByUuid(uuid: $uuid) {
      id
      uuid
      name
      description
      meta
      steps
      audioEnabled
      hasAudio
      createdBy {
        firstName
        lastName
        id
      }
      skillSet {
        id
        name
        uuid
        isInMyLibrary
        isInMyOrganizationLibrary
        sharedAccess
        canEditSharing
      }
      isOwner
      isInMyLibrary
      isInMyOrganizationLibrary
      canEdit
      canMove
      canEditSharing
      createdAt
      canDelete
      sharedAccess
      completed
      rrwebEventsUrl
      audioUrl
      liveLearning
      labs
      embedEnabled
      viewCount
      interactionCount
    }
  }
`

export const GET_COMPLETED_WALKTHROUGH = gql`
  query getCompletedWalkthroughs(
    $page: Int
    $perPage: Int
    $sortBy: String
    $sortDirection: String
    $contentType: ContentType
  ) {
    items: completed(
      page: $page
      perPage: $perPage
      sortBy: $sortBy
      sortDirection: $sortDirection
      contentType: $contentType
    ) {
      ... on Walkthrough {
        id
        description
        name
        steps
        uuid
        meta
        createdBy {
          firstName
          lastName
          id
        }
        isOwner
        isInMyLibrary
        isInMyOrganizationLibrary
        canEditSharing
        canDelete
        sharedAccess
        createdAt
        updatedAt
        completed
        canEdit
        canMove
        createdAt
        canEditSharing
        viewCount
        interactionCount
        skillSet {
          id
          name
          uuid
          sharedAccess
          __typename
        }
        __typename
      }
      ... on SkillSet {
        id
        name
        uuid
        assignedUserIds
        completed
        canEdit
        canMove
        isAssigned
        isInMyLibrary
        isInMyOrganizationLibrary
        sharedAccess
        createdAt
        isInMyLibrary
        isInMyOrganizationLibrary
        canEditSharing
        walkthroughs {
          id
          name
          uuid
          steps
          completed
          meta
        }
        __typename
      }
    }
    total: completedMeta(contentType: $contentType) {
      count
    }
  }
`

export interface WalkthroughData {
  walkthrough: Walkthrough
}

export interface WalkthroughVars {
  uuid: string
}

export const DELETE_WALKTHROUGH_QUERY = gql`
  mutation deleteWalkthrough($id: ID!, $organizationId: ID) {
    deleteWalkthrough(id: $id, organizationId: $organizationId) {
      id
    }
  }
`

export const MOVE_WALKTHROUGH_QUERY = gql`
  mutation moveWalkthrough($id: ID!, $destinationType: DestinationType!, $destinationId: ID) {
    walkthrough: moveWalkthrough(
      id: $id
      destinationType: $destinationType
      destinationId: $destinationId
    ) {
      id
      uuid
      name
      isInMyLibrary
      isInMyOrganizationLibrary
      skillSet {
        uuid
      }
    }
  }
`

export const UPDATE_WALKTHROUGH_QUERY = gql`
  mutation updateWalkthrough(
    $id: ID!
    $name: String!
    $description: String!
    $steps: [JSON!]!
    $liveLearning: Boolean
    $labs: Boolean
    $audioEnabled: Boolean
  ) {
    updateWalkthrough(
      id: $id
      name: $name
      description: $description
      steps: $steps
      liveLearning: $liveLearning
      labs: $labs
      audioEnabled: $audioEnabled
    ) {
      id
      uuid
      description
      name
      steps
      audioEnabled
      audioUrl
    }
  }
`

export const SHARE_WALKTHROUGH_QUERY = gql`
  mutation shareWalkthrough($id: ID!, $shareType: ShareTypeEnum!) {
    shareWalkthrough(id: $id, shareType: $shareType) {
      id
      name
      sharedAccess
      isInMyOrganizationLibrary
      isInMyLibrary
    }
  }
`

export const SHARE_EMBED_WALKTHROUGH_QUERY = gql`
  mutation shareEmbedWalkthrough($id: ID!, $enabled: Boolean!) {
    shareEmbedWalkthrough(id: $id, enabled: $enabled) {
      id
    }
  }
`

export const GET_RECENTLY_VIEWED_WALKTHROUGHS = gql`
  query getRecentlyViewedWalkthroughs(
    $page: Int
    $perPage: Int
    $sortBy: String
    $sortDirection: String
    $contentType: ContentType
  ) {
    items: recentlyViewed(
      page: $page
      perPage: $perPage
      sortBy: $sortBy
      sortDirection: $sortDirection
      contentType: $contentType
    ) {
      ... on Walkthrough {
        id
        description
        name
        steps
        uuid
        sharedAccess
        isInMyLibrary
        isInMyOrganizationLibrary
        createdAt
        updatedAt
        completed
        canEdit
        canMove
        canEditSharing
        meta
        viewCount
        interactionCount
        skillSet {
          id
          name
          uuid
          sharedAccess
          __typename
        }
        __typename
      }
      ... on SkillSet {
        id
        name
        uuid
        assignedUserIds
        completed
        canEdit
        canMove
        isAssigned
        isInMyLibrary
        isInMyOrganizationLibrary
        sharedAccess
        canEditSharing
        isInMyLibrary
        isInMyOrganizationLibrary
        walkthroughs {
          id
          name
          steps
          uuid
          completed
          meta
        }
        __typename
      }
    }
    total: recentlyViewedMeta(contentType: $contentType) {
      count
    }
  }
`

export const GET_SHARED_WITH_ME_WALKTHROUGHS = gql`
  query getWalkthroughsSharedWithMe(
    $page: Int
    $perPage: Int
    $sortBy: String
    $sortDirection: String
    $contentType: ContentType
  ) {
    items: sharedWithMe(
      page: $page
      perPage: $perPage
      sortBy: $sortBy
      sortDirection: $sortDirection
      contentType: $contentType
    ) {
      ... on Walkthrough {
        id
        description
        name
        steps
        uuid
        meta
        createdBy {
          firstName
          lastName
          id
        }
        sharedAccess
        isInMyLibrary
        isInMyOrganizationLibrary
        createdAt
        updatedAt
        completed
        canEdit
        canMove
        canEditSharing
        viewCount
        interactionCount
        skillSet {
          id
          name
          uuid
          sharedAccess
          __typename
        }
        __typename
      }
      ... on SkillSet {
        id
        name
        uuid
        assignedUserIds
        completed
        canEdit
        canMove
        isAssigned
        isInMyLibrary
        isInMyOrganizationLibrary
        sharedAccess
        isInMyLibrary
        isInMyOrganizationLibrary
        canEditSharing
        walkthroughs {
          id
          name
          uuid
          steps
          meta
          completed
        }
        __typename
      }
    }
    total: sharedWithMeMeta(contentType: $contentType) {
      count
    }
  }
`

export const GET_ORGANIZATION_WALKTHROUGHS = gql`
  query getOrganizationWalkthroughs(
    $page: Int
    $perPage: Int
    $sortBy: String
    $sortDirection: String
    $contentType: ContentType
  ) {
    items: organizationLibrary(
      page: $page
      perPage: $perPage
      sortBy: $sortBy
      sortDirection: $sortDirection
      contentType: $contentType
    ) {
      ... on Walkthrough {
        id
        description
        name
        steps
        uuid
        sharedAccess
        createdAt
        updatedAt
        completed
        canEdit
        canMove
        isInMyLibrary
        isInMyOrganizationLibrary
        canEditSharing
        meta
        viewCount
        interactionCount
        skillSet {
          id
          name
          uuid
          sharedAccess
          __typename
        }
        __typename
      }
      ... on SkillSet {
        id
        name
        uuid
        assignedUserIds
        completed
        canEdit
        canMove
        isAssigned
        isInMyLibrary
        isInMyOrganizationLibrary
        sharedAccess
        isInMyLibrary
        isInMyOrganizationLibrary
        canEditSharing
        walkthroughs {
          id
          name
          uuid
          steps
          completed
          meta
        }
        __typename
      }
    }
    total: organizationLibraryMeta(contentType: $contentType) {
      count
    }
  }
`

export const GET_GALLERY_CONTENTS_QUERY = gql`
  query getGalleryContents(
    $page: Int
    $perPage: Int
    $sortBy: String
    $sortDirection: String
    $contentType: ContentType
  ) {
    items: galleryContents(
      page: $page
      perPage: $perPage
      sortBy: $sortBy
      sortDirection: $sortDirection
      contentType: $contentType
    ) {
      ... on Walkthrough {
        id
        description
        name
        steps
        uuid
        sharedAccess
        isInMyLibrary
        isInMyOrganizationLibrary
        createdAt
        updatedAt
        completed
        canEdit
        canMove
        canEditSharing
        viewCount
        interactionCount
        meta
        skillSet {
          id
          name
          uuid
          sharedAccess
          __typename
        }
        __typename
      }
      ... on SkillSet {
        id
        name
        uuid
        assignedUserIds
        completed
        canEdit
        canMove
        isAssigned
        isInMyLibrary
        isInMyOrganizationLibrary
        sharedAccess
        canEditSharing
        isInMyLibrary
        isInMyOrganizationLibrary
        walkthroughs {
          id
          name
          steps
          uuid
          meta
          completed
        }
        __typename
      }
    }
    total: galleryContentsMeta(contentType: $contentType) {
      count
    }
  }
`
